import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from '../../../../../../../ui/toast';
import { ADMIN_PAGES_KEYS, NS } from '../../../../../../../i18n/constants/keys';
import { addCandidatesToOffer } from '../../../../../../../utils/api_service/JobOpenings';

export const EmptyState = ({
  jobOpeningId,
}: {
  jobOpeningId: string;
  addCandidatesFromPreviousRound: (candidateIds: string[]) => void;
}) => {
  const { t } = useTranslation(NS.adminPages);
  const ES = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.emptyState;
  const [isAddingCandidates, setIsAddingCandidates] = useState(false);

  const handleAddCandidates = async () => {
    setIsAddingCandidates(true);
    try {
      await addCandidatesToOffer(jobOpeningId);
      toast.success(t(ES.successToast), {
        position: 'bottom-center',
      });
      window.location.reload(); // Reload to show the newly added candidates
    } catch (err) {
      toast.error(t(ES.failedToast), {
        position: 'bottom-center',
      });
    } finally {
      setIsAddingCandidates(false);
    }
  };

  return (
    <div className='col-span-full bg-white rounded-3xl shadow-lg border border-gray-200 p-12 text-center'>
      <div className='flex flex-col items-center max-w-md mx-auto'>
        { /* Empty state illustration */ }
        <div className='mb-6 w-24 h-24 rounded-full bg-indigo-50 flex items-center justify-center'>
          <svg
              className='w-12 h-12 text-indigo-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z'
            />
          </svg>
        </div>

        <h2 className='text-2xl font-semibold text-gray-800 mb-3'>
          { t(ES.title) }
        </h2>

        <p className='text-gray-600 mb-8'>
          { t(ES.subtitle) }
        </p>

        <button
            onClick={handleAddCandidates}
            disabled={isAddingCandidates}
            className='px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-medium rounded-xl shadow-md transition-all disabled:opacity-50 disabled:cursor-not-allowed'
        >
          { isAddingCandidates ? (
            <div className='flex items-center'>
              <div className='animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-3'></div>
              { t(ES.adding) }
            </div>
          ) : (
            <div className='flex items-center'>
              <svg
                  className='w-5 h-5 mr-2'
                  fill='none'
                  stroke='currentColor'
                  viewBox='0 0 24 24'
                  xmlns='http://www.w3.org/2000/svg'
              >
                <path
                    strokeLinecap='round'
                    strokeLinejoin='round'
                    strokeWidth={2}
                    d='M12 6v6m0 0v6m0-6h6m-6 0H6'
                />
              </svg>
              { t(ES.addButton) }
            </div>
          ) }
        </button>
      </div>
    </div>
  );
};

export const ErrorFallback = ({ error }: { error: string }) => {
  const { t } = useTranslation(NS.adminPages);
  const ERR = ADMIN_PAGES_KEYS.jobOpenings.smartInterviews.offer.emptyState.error;
  return (
    <div className='col-span-full bg-white bg-opacity-95 backdrop-blur-sm rounded-3xl shadow-lg border border-gray-200 overflow-hidden'>
      <div className='flex flex-col items-center justify-center text-center px-6 py-16 md:py-20'>
        { /* Error Icon with animated pulse */ }
        <div className='mb-6 w-24 h-24 rounded-full bg-red-50 flex items-center justify-center animate-pulse'>
          <svg
              className='w-12 h-12 text-red-500'
              fill='none'
              stroke='currentColor'
              viewBox='0 0 24 24'
              xmlns='http://www.w3.org/2000/svg'
          >
            <path
                strokeLinecap='round'
                strokeLinejoin='round'
                strokeWidth={1.5}
                d='M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z'
            />
          </svg>
        </div>

        { /* Error Title */ }
        <h2 className='text-2xl font-semibold text-gray-800 mb-3'>
          { t(ERR.title) }
        </h2>

        { /* Helpful Suggestions */ }
        <div className='bg-blue-50 rounded-xl p-4 mb-8 max-w-md'>
          <h3 className='font-medium text-blue-700 mb-2'>{ t(ERR.suggestedActions) }</h3>
          <ul className='text-sm text-gray-700 text-left list-disc pl-5 space-y-1'>
            <li>{ t(ERR.checkResults) }</li>
            <li>{ t(ERR.verifyId) }</li>
            <li>{ t(ERR.tryRefreshing) }</li>
          </ul>
        </div>

        { /* Action Buttons */ }
      </div>
    </div>
  );
};
